// This modal is to edit the seqeucen of trainigns and can be accessed from trainings page in the admin hub
import React, { useState, useEffect } from 'react'
import { Training, TrainingSequenceEditMetaProps } from '../data/types'
import { Button, Modal } from './ui'
import { arrayMove } from '@dnd-kit/sortable'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';


type Props = {
    meta: TrainingSequenceEditMetaProps,
    isOpen: boolean
    trainings: Training[]
    onClose: () => void
    onSubmit: (sortedTrainings: Training[]) => void
}

function SortableItem({ item }: { item: Training }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: item.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        padding: '12px',
        margin: '8px 0',
        borderRadius: '4px',
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1, // Visual feedback
        zIndex: isDragging ? 999 : 1
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="draggable-item bg-white dark:bg-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600"
        >
            {item.name}
        </div>
    )
}


export const TrainingSequenceEditModal = (props: Props) => {
    const { meta, isOpen, trainings, onClose, onSubmit } = props

    const [loading, setLoading] = useState(false)
    const [trainingsCopy, setTrainingsCopy] = useState([...trainings])
    const [originalTrainings, setOriginalTrainings] = useState<Training[]>([])

    const [error, setError] = useState<String | null>(null)

    // Mount Trainings when they are loaded
    useEffect(() => {
        if (!isOpen) {
            setError(null)
            return
        }

        if (trainings.length > 0) {
            setTrainingsCopy([...trainings])
            setOriginalTrainings([...trainings])
        }

    }, [trainings, isOpen]);


    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        setTrainingsCopy((prev) => {
            const oldIndex = prev.findIndex(i => i.id === active.id);
            const newIndex = prev.findIndex(i => i.id === over.id);

            return arrayMove(prev, oldIndex, newIndex);
        });
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)

        try {
            await onSubmit(trainingsCopy)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : "Error while updating trainings' sequence.")
        } finally {
            setLoading(false)
        }
    }

    const handleSort = (sortType: number) => {
        setTrainingsCopy(prev => {
            switch (sortType) {
                case 0:
                    return [...prev].sort((a, b) => a.name.localeCompare(b.name))
                case 1:
                    return [...prev].sort((a, b) => b.name.localeCompare(a.name))
                case 2:
                    return [...originalTrainings]
                default:
                    return prev
            }
        })
    }



    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={meta.title}
                maxWidth='2xl'
                footer={
                    <div className="flex gap-3 justify-end">
                        <Button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            variant="outline"
                            color="neutral"
                        >
                            {meta.cancelButton}
                        </Button>
                        <Button
                            type="submit"
                            form="sequence-form"
                            disabled={loading}
                        >
                            {loading ? meta.submittingButton : meta.submitButton}
                        </Button>
                    </div>
                }
            >
                <div className='p-3'>

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    <div className='flex justify-between m-2'>
                        <div className='flex gap-4'>
                            <Button variant='outline' type='button' onClick={() => handleSort(0)}>Sort: A-Z</Button>
                            <Button variant='outline' type='button' onClick={() => handleSort(1)}>Sort: Z-A</Button>
                        </div>
                        <div>
                            <Button variant='outline' type='button' onClick={() => handleSort(2)}>Reset</Button>
                        </div>
                    </div>

                    <div className='border-t-2 pt-2 dark:border-gray-700'>
                        <p className='text-sm text-gray-500 italic'>{meta.message}</p>
                    </div>

                    <form id='sequence-form' onSubmit={handleSubmit}>
                        <DndContext
                            key={trainingsCopy.map(t => t.id).join(',')}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                        >
                            <SortableContext
                                items={trainingsCopy.map(i => i.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {trainingsCopy.map(item => (
                                    <SortableItem key={item.id} item={item} />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </form>
                </div >
            </Modal>

        </>
    )

}